// src/services/remoteConfigService.js
import { getRemoteConfig, getValue, fetchAndActivate, getAll } from "firebase/remote-config";
import { app } from "./firebase.js";
import { logger } from "../utils/logger";

/**
 * @file This service provides functions for fetching data from Firebase Remote Config.
 */

const remoteConfig = getRemoteConfig(app);

// It's good practice to set default values
remoteConfig.defaultConfig = {
  "main_image_url": "",
  "big6_extended_data": "{}",
  "plansEnabled": true
};

// Cache for plansEnabled to avoid repeated remote config calls
let plansEnabledCache = null;
let plansEnabledCacheTime = 0;
const CACHE_DURATION = 5 * 60 * 1000; // 5 minutes

// Helper to measure performance of async operations
const measureRemoteConfigPerformance = async (operationName, promiseFn) => {
  const start = performance.now();
  try {
    const result = await promiseFn();
    const end = performance.now();
    logger.log(`remoteConfigService: ${operationName} completed in ${(end - start).toFixed(2)} ms`);
    return result;
  } catch (error) {
    const end = performance.now();
    logger.error(`remoteConfigService: ${operationName} failed in ${(end - start).toFixed(2)} ms`, error);
    throw error;
  }
};

/**
 * Gets the main image URL from Firebase Remote Config.
 *
 * @returns {Promise<string>} A promise that resolves with the main image URL.
 */
export const getMainImageUrl = async () => {
  try {
    await measureRemoteConfigPerformance("fetchAndActivate", () => fetchAndActivate(remoteConfig));
    return getValue(remoteConfig, "main_image_url").asString();
  } catch (err) {
    logger.error("remoteConfigService: Failed to fetch remote config main_image_url:", err);
    return ""; // Return a default or empty string on error
  }
};

/**
 * Gets the "big 6" teams data from Firebase Remote Config.
 *
 * @returns {Promise<object>} A promise that resolves with the "big 6" teams data.
 */
export const getBig6TeamsData = async () => {
  try {
    await measureRemoteConfigPerformance("fetchAndActivate", () => fetchAndActivate(remoteConfig));
    const big6DataString = getValue(remoteConfig, "big6_extended_data").asString();
    return JSON.parse(big6DataString || "{}");
  } catch (err) {
    logger.error("remoteConfigService: Failed to fetch remote config big6_extended_data:", err);
    return {}; // Return an empty object on error
  }
};

/**
 * Gets the plans enabled flag from Firebase Remote Config with caching.
 *
 * @returns {Promise<boolean>} A promise that resolves with the plans enabled flag.
 */
export const getPlansEnabled = async () => {
  const now = Date.now();
  
  // Return cached value if still valid
  if (plansEnabledCache !== null && (now - plansEnabledCacheTime) < CACHE_DURATION) {
    logger.log(`remoteConfigService: Returning cached plansEnabled: ${plansEnabledCache}`);
    return plansEnabledCache;
  }

  try {
    await measureRemoteConfigPerformance("fetchAndActivate", () => fetchAndActivate(remoteConfig));
    const plansEnabled = getValue(remoteConfig, "plansEnabled").asBoolean();
    
    // Cache the result
    plansEnabledCache = plansEnabled;
    plansEnabledCacheTime = now;
    
    logger.log(`remoteConfigService: Fetched and cached plansEnabled: ${plansEnabled}`);
    return plansEnabled;
  } catch (err) {
    logger.error("remoteConfigService: Failed to fetch remote config plansEnabled:", err);
    return true; // Return default value (plans enabled) on error
  }
};

/**
 * Gets the enable_smart_highlights flag from Firebase Remote Config.
 *
 * @returns {Promise<boolean>} A promise that resolves with the enable_smart_highlights flag.
 */
export const getEnableSmartHighlights = async () => {
  try {
    await measureRemoteConfigPerformance("fetchAndActivate", () => fetchAndActivate(remoteConfig));
    return getValue(remoteConfig, "enable_smart_highlights").asBoolean();
  } catch (err) {
    logger.error("remoteConfigService: Failed to fetch remote config enable_smart_highlights:", err);
    return false; // Default to false (Curator legacy flow)
  }
};

/**
 * Fetches and parses the feature lists for the subscription plans.
 * @returns {Promise<{fan: string[], fan_plus: string[]}>} An object containing the feature lists.
 */
export async function getPlanFeatures() {
  try {
    await fetchAndActivate(remoteConfig);
    const allConfig = getAll(remoteConfig);

    const fanFeatures = JSON.parse(allConfig.fan_plan_features?.asString() || '[]');
    const fanPlusFeatures = JSON.parse(allConfig.fan_plus_plan_features?.asString() || '[]');

    return {
      fan: fanFeatures,
      fan_plus: fanPlusFeatures,
    };
  } catch (error) {
    console.error("Error fetching plan features from Remote Config:", error);
    return { fan: [], fan_plus: [] }; // Return empty arrays on error
  }
};
